import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { useI18n } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full bg-transparent border-b border-border py-3 text-primary placeholder:text-muted-foreground/70 focus:border-accent focus:outline-none transition-colors font-sans text-[15px]";
const Label = ({ children, required }) => (
  <span className="block text-xs uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-1.5 font-sans">{children}{required && <span className="text-accent ml-1">*</span>}</span>
);

// Aftercare enquiry — reuses the existing /api/case-reviews endpoint.
export default function AftercareEnquiryForm({ f }) {
  const { lang, t } = useI18n();
  const [form, setForm] = useState({ full_name: "", country: "", email: "", phone: "", procedure: "", city: "", surgery_date: "", arrival: "", departure: "", companions: "", companions_count: "", mobility: "", language: "", additional: "", consent: false });
  const [support, setSupport] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggle = (opt) => setSupport((s) => (s.includes(opt) ? s.filter((x) => x !== opt) : [...s, opt]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) || !form.consent) {
      toast.error(t("form.errors.submit"));
      return;
    }
    setSubmitting(true);
    const message = [
      "[AFTERCARE ENQUIRY]",
      `Procedure: ${form.procedure || "—"}`,
      `Treatment city: ${form.city || "—"}`,
      `Surgery date: ${form.surgery_date || "—"}`,
      `Arrival: ${form.arrival || "—"} | Departure: ${form.departure || "—"}`,
      `Travelling: ${form.companions || "—"} (companions: ${form.companions_count || "0"})`,
      `Mobility: ${form.mobility || "—"}`,
      `Preferred language: ${form.language || "—"}`,
      `Support required: ${support.join(", ") || "—"}`,
      `Additional: ${form.additional || "—"}`,
    ].join("\n");
    try {
      await axios.post(`${API}/case-reviews`, {
        full_name: form.full_name, country: form.country || "Not provided", email: form.email, phone: (form.phone && form.phone.trim().length >= 3) ? form.phone : "Not provided",
        preferred_contact: "email", procedure_category: form.procedure || "aftercare", procedure_type: "aftercare-enquiry",
        preferred_month: form.surgery_date || "", message, photo_ids: [], consent: true, language: lang,
      });
      setDone(true);
      toast.success(f.success);
    } catch (err) {
      toast.error(t("form.errors.submit"));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-surface border border-border rounded-lg p-8" data-testid="aftercare-success">
        <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center mb-4"><Check className="w-6 h-6 text-accent" /></div>
        <p className="text-[16px] text-secondary-foreground leading-relaxed">{f.success}</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-surface border border-border rounded-lg p-6 md:p-8" data-testid="aftercare-form" noValidate>
      <h3 className="font-serif text-2xl text-primary mb-2">{f.heading}</h3>
      <p className="text-[14px] text-muted-foreground mb-6">{f.intro}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        <label><Label required>{f.fullName}</Label><input className={field} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="ac-name" /></label>
        <label><Label>{f.country}</Label><input className={field} value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="ac-country" /></label>
        <label><Label required>{f.email}</Label><input type="email" className={field} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="ac-email" /></label>
        <label><Label>{f.phone}</Label><input className={field} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="ac-phone" /></label>
        <label><Label>{f.procedure}</Label><input className={field} value={form.procedure} onChange={(e) => set("procedure", e.target.value)} data-testid="ac-procedure" /></label>
        <label><Label>{f.city}</Label><input className={field} value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="ac-city" /></label>
        <label><Label>{f.surgeryDate}</Label><input className={field} placeholder="YYYY-MM" value={form.surgery_date} onChange={(e) => set("surgery_date", e.target.value)} data-testid="ac-surgery-date" /></label>
        <label><Label>{f.arrival}</Label><input className={field} placeholder="YYYY-MM-DD" value={form.arrival} onChange={(e) => set("arrival", e.target.value)} data-testid="ac-arrival" /></label>
        <label><Label>{f.departure}</Label><input className={field} placeholder="YYYY-MM-DD" value={form.departure} onChange={(e) => set("departure", e.target.value)} data-testid="ac-departure" /></label>
        <label><Label>{f.companions}</Label>
          <select className={field} value={form.companions} onChange={(e) => set("companions", e.target.value)} data-testid="ac-companions">
            <option value="">—</option>
            <option value={f.companionOptions.alone}>{f.companionOptions.alone}</option>
            <option value={f.companionOptions.accompanied}>{f.companionOptions.accompanied}</option>
          </select>
        </label>
        <label><Label>{f.companionsCount}</Label><input type="number" min="0" className={field} value={form.companions_count} onChange={(e) => set("companions_count", e.target.value)} data-testid="ac-companions-count" /></label>
        <label><Label>{f.language}</Label><input className={field} value={form.language} onChange={(e) => set("language", e.target.value)} data-testid="ac-language" /></label>
        <label className="md:col-span-2"><Label>{f.mobility}</Label><input className={field} value={form.mobility} onChange={(e) => set("mobility", e.target.value)} data-testid="ac-mobility" /></label>
      </div>

      <div className="mt-6">
        <Label>{f.support}</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          {f.supportOptions.map((opt, i) => (
            <label key={opt} className="flex items-center gap-2.5 text-[14px] text-secondary-foreground cursor-pointer">
              <Checkbox checked={support.includes(opt)} onCheckedChange={() => toggle(opt)} data-testid={`ac-support-${i}`} /> {opt}
            </label>
          ))}
        </div>
      </div>

      <label className="block mt-6"><Label>{f.additional}</Label><textarea rows={3} className={`${field} resize-none`} value={form.additional} onChange={(e) => set("additional", e.target.value)} data-testid="ac-additional" /></label>

      <div className="flex items-start gap-3 mt-6">
        <Checkbox id="ac-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-1" data-testid="ac-consent" />
        <label htmlFor="ac-consent" className="text-[13px] text-secondary-foreground leading-relaxed cursor-pointer font-sans">{f.consent}</label>
      </div>

      <button type="submit" disabled={submitting} className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-8 py-4 font-medium font-sans whitespace-nowrap hover:bg-primary/90 transition-all active:scale-[0.97] disabled:opacity-60" data-testid="ac-submit">
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}{f.submit}
      </button>
    </form>
  );
}
